/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/DisaEntrySections/SettingsSection.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Settings section component for DISA Entry form.
────────────────────────────────────────────────────────────────*/

import { Settings } from 'lucide-react';
import { useI18n } from '../../../../hooks/useI18n';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import Toggle from '../../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../../components/ui/SimpleTooltip';
import { FormBox } from '../../../../components/ui/FormBox';
import RecordingSelect from '../../../../components/ui/RecordingSelect';
import { CallerIdInput } from '../../../../components/ui/CallerIdInput';
import { FormState, ValidationErrors } from '../../types';
import { DEFAULT_SETTINGS } from '../../utils/formDefaults';

interface SettingsSectionProps {
  formData: FormState;
  errors: ValidationErrors;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSettingsChange: (key: keyof FormState['settings'], value: any) => void;
}

export function SettingsSection({
  formData,
  errors,
  onSettingsChange
}: SettingsSectionProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('disaEntries:sections.settings')} icon={<Settings className="h-5 w-5" />} className="mt-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
        <Label htmlFor="dialPrefix">
          <SimpleTooltip content={t('disaEntries:fields.dialPrefix.tooltip')}>
            {t('disaEntries:fields.dialPrefix.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Input
            id="dialPrefix"
            value={formData.settings.dialPrefix}
            onChange={(event) => onSettingsChange('dialPrefix', event.target.value)}
            placeholder={t('disaEntries:fields.dialPrefix.placeholder')}
          />
        </div>

        <Label htmlFor="callerId">
          <SimpleTooltip content={t('disaEntries:fields.callerId.tooltip')}>
            {t('disaEntries:fields.callerId.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <CallerIdInput
            id="callerId"
            nameValue={formData.settings.callerIdName}
            numberValue={formData.settings.callerIdNumber}
            onNameChange={(value) => onSettingsChange('callerIdName', value)}
            onNumberChange={(value) => onSettingsChange('callerIdNumber', value)}
            namePlaceholder={t('disaEntries:fields.callerId.namePlaceholder')}
            numberPlaceholder={t('disaEntries:fields.callerId.numberPlaceholder')}
            nameTooltip={t('disaEntries:fields.callerId.nameTooltip')}
            numberTooltip={t('disaEntries:fields.callerId.numberTooltip')}
            disabled={formData.settings.inheritCallerId}
          />
        </div>

        <Label>
          <SimpleTooltip content={t('disaEntries:fields.inheritCallerId.tooltip')}>
            {t('disaEntries:fields.inheritCallerId.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle
            value={formData.settings.inheritCallerId}
            onChange={(value) => onSettingsChange('inheritCallerId', value)}
          />
        </div>

        <Label htmlFor="greeting">
          <SimpleTooltip content={t('disaEntries:fields.greeting.tooltip')}>
            {t('disaEntries:fields.greeting.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <RecordingSelect
            value={formData.settings.greeting}
            onChange={(value) => onSettingsChange('greeting', value || '')}
            placeholder={t('disaEntries:fields.greeting.placeholder')}
            returnType="id"
            showPlayButton
          />
        </div>

        <Label htmlFor="invalidPinSound">
          <SimpleTooltip content={t('disaEntries:fields.invalidPinSound.tooltip')}>
            {t('disaEntries:fields.invalidPinSound.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Input
            id="invalidPinSound"
            value={formData.settings.invalidPinSound}
            onChange={(event) => onSettingsChange('invalidPinSound', event.target.value)}
            placeholder={t('disaEntries:fields.invalidPinSound.placeholder')}
          />
        </div>

        <Label htmlFor="timeoutSound">
          <SimpleTooltip content={t('disaEntries:fields.timeoutSound.tooltip')}>
            {t('disaEntries:fields.timeoutSound.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Input
            id="timeoutSound"
            value={formData.settings.timeoutSound}
            onChange={(event) => onSettingsChange('timeoutSound', event.target.value)}
            placeholder={t('disaEntries:fields.timeoutSound.placeholder')}
          />
        </div>

        <Label htmlFor="responseTimeout">
          <SimpleTooltip content={t('disaEntries:fields.responseTimeout.tooltip')}>
            {t('disaEntries:fields.responseTimeout.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Input
            id="responseTimeout"
            type="number"
            min={1}
            max={60}
            value={formData.settings.responseTimeout}
            onChange={(event) => onSettingsChange('responseTimeout', Number(event.target.value) || DEFAULT_SETTINGS.responseTimeout)}
            className={errors.responseTimeout ? 'border-red-500' : ''}
          />
          {errors.responseTimeout && <p className="text-sm text-red-500 mt-1">{errors.responseTimeout}</p>}
        </div>

        <Label htmlFor="digitTimeout">
          <SimpleTooltip content={t('disaEntries:fields.digitTimeout.tooltip')}>
            {t('disaEntries:fields.digitTimeout.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Input
            id="digitTimeout"
            type="number"
            min={1}
            max={10}
            value={formData.settings.digitTimeout}
            onChange={(event) => onSettingsChange('digitTimeout', Number(event.target.value) || DEFAULT_SETTINGS.digitTimeout)}
            className={errors.digitTimeout ? 'border-red-500' : ''}
          />
          {errors.digitTimeout && <p className="text-sm text-red-500 mt-1">{errors.digitTimeout}</p>}
        </div>

        <Label htmlFor="maxCallDuration">
          <SimpleTooltip content={t('disaEntries:fields.maxCallDuration.tooltip')}>
            {t('disaEntries:fields.maxCallDuration.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Input
            id="maxCallDuration"
            type="number"
            min={60}
            max={3600}
            value={formData.settings.maxCallDuration}
            onChange={(event) => onSettingsChange('maxCallDuration', Number(event.target.value) || DEFAULT_SETTINGS.maxCallDuration)}
            className={errors.maxCallDuration ? 'border-red-500' : ''}
          />
          {errors.maxCallDuration && <p className="text-sm text-red-500 mt-1">{errors.maxCallDuration}</p>}
        </div>

        <Label htmlFor="maxAttempts">
          <SimpleTooltip content={t('disaEntries:fields.maxAttempts.tooltip')}>
            {t('disaEntries:fields.maxAttempts.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Input
            id="maxAttempts"
            type="number"
            min={1}
            max={5}
            value={formData.settings.maxAttempts}
            onChange={(event) => onSettingsChange('maxAttempts', Number(event.target.value) || DEFAULT_SETTINGS.maxAttempts)}
            className={errors.maxAttempts ? 'border-red-500' : ''}
          />
          {errors.maxAttempts && <p className="text-sm text-red-500 mt-1">{errors.maxAttempts}</p>}
        </div>

        <Label>
          <SimpleTooltip content={t('disaEntries:fields.hangupOnFail.tooltip')}>
            {t('disaEntries:fields.hangupOnFail.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle value={formData.settings.hangupOnFail} onChange={(value) => onSettingsChange('hangupOnFail', value)} />
        </div>

        <Label>
          <SimpleTooltip content={t('disaEntries:fields.recordCall.tooltip')}>
            {t('disaEntries:fields.recordCall.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle value={formData.settings.recordCall} onChange={(value) => onSettingsChange('recordCall', value)} />
        </div>

        <Label>
          <SimpleTooltip content={t('disaEntries:fields.logAccess.tooltip')}>
            {t('disaEntries:fields.logAccess.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle value={formData.settings.logAccess} onChange={(value) => onSettingsChange('logAccess', value)} />
        </div>

        <Label>
          <SimpleTooltip content={t('disaEntries:fields.allowLocal.tooltip')}>
            {t('disaEntries:fields.allowLocal.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle value={formData.settings.allowLocal} onChange={(value) => onSettingsChange('allowLocal', value)} />
        </div>

        <Label>
          <SimpleTooltip content={t('disaEntries:fields.allowInternational.tooltip')}>
            {t('disaEntries:fields.allowInternational.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle
            value={formData.settings.allowInternational}
            onChange={(value) => onSettingsChange('allowInternational', value)}
          />
        </div>

        <Label>
          <SimpleTooltip content={t('disaEntries:fields.retryOnFail.tooltip')}>
            {t('disaEntries:fields.retryOnFail.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle value={formData.settings.retryOnFail} onChange={(value) => onSettingsChange('retryOnFail', value)} />
        </div>

        <Label htmlFor="notifyOnAccess">
          <SimpleTooltip content={t('disaEntries:fields.notifyOnAccess.tooltip')}>
            {t('disaEntries:fields.notifyOnAccess.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Input
            id="notifyOnAccess"
            value={formData.settings.notifyOnAccess}
            onChange={(event) => onSettingsChange('notifyOnAccess', event.target.value)}
            placeholder={t('disaEntries:fields.notifyOnAccess.placeholder')}
          />
        </div>
      </div>
    </FormBox>
  );
}

